import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { clearActiveOnLogout } from '../services/zanaChatStore';
import { clearLocationDek, ensureLocationDek } from '../utils/locationCrypto';

interface AuthContextType {
  currentUser: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Re-resolve admin/fleet role from Firestore for the current user (e.g. just after self-serve provisioning). */
  refreshAdminStatus: () => Promise<void>;
  isAdmin: boolean;
  isFleetAdmin: boolean;
  fleetOrgCode: string | null;
  /** False until the saved session has been read and the admin role decided.
   *  Route guards wait for it: deciding earlier sent every page load to the
   *  sign-in page and then on to Overview (Gabriel, 2026-10-28). */
  authReady: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isFleetAdmin, setIsFleetAdmin] = useState(false);
  const [fleetOrgCode, setFleetOrgCode] = useState<string | null>(null);
  const [authReady, setAuthReady] = useState(false);

  const resolveAdminStatus = async (user: User | null) => {
    if (!user) {
      setIsAdmin(false);
      setIsFleetAdmin(false);
      setFleetOrgCode(null);
      return;
    }
    // Platform (console) super-admin: a backend-written platformAdmins/{uid} doc,
    // not an email substring. Unforgeable — an attacker can't self-grant it.
    try {
      const adminSnap = await getDoc(doc(db, 'platformAdmins', user.uid));
      if (adminSnap.exists()) {
        setIsAdmin(true);
        setIsFleetAdmin(false);
        setFleetOrgCode(null);
        setAuthReady(true);
        await ensureLocationDek();
        return;
      }
    } catch { /* fall through to the fleet-admin check */ }
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const data = userDoc.data();
      const fleetOrg =
        (data?.orgCode as string) ||
        (data?.schoolCode as string) ||
        (data?.organizationCode as string) ||
        '';
      if (data?.role === 'fleet_admin' && fleetOrg) {
        setIsAdmin(true);
        setIsFleetAdmin(true);
        setFleetOrgCode(fleetOrg);
        setAuthReady(true);
        await ensureLocationDek(fleetOrg);
      } else {
        setIsAdmin(false);
        setIsFleetAdmin(false);
        setFleetOrgCode(null);
        setAuthReady(true);
        await ensureLocationDek();
      }
    } catch {
      setIsAdmin(false);
      setIsFleetAdmin(false);
      setFleetOrgCode(null);
      setAuthReady(true);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const user = cred.user;
      await user.reload();
      // Platform (console) super-admin: a backend-written platformAdmins/{uid} doc,
      // not an email substring. Unforgeable — an attacker can't self-grant it.
      let isSuperAdmin = false;
      try {
        const adminSnap = await getDoc(doc(db, 'platformAdmins', user.uid));
        isSuperAdmin = adminSnap.exists();
      } catch { isSuperAdmin = false; }
      let isFleet = false;
      let orgCode: string | null = null;
      let fleetCheckError: string | null = null;
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const data = userDoc.data();
        const fleetOrg =
          (data?.orgCode as string) ||
          (data?.schoolCode as string) ||
          (data?.organizationCode as string) ||
          '';
        if (data?.role === 'fleet_admin' && fleetOrg) {
          isFleet = true;
          orgCode = fleetOrg;
        } else if (!isSuperAdmin) {
          if (!userDoc.exists()) {
            fleetCheckError = 'No fleet admin account found for this email. Create one using "Create account" and your organization code.';
          } else if (data?.role !== 'fleet_admin') {
            fleetCheckError = 'This account is not a fleet admin. Contact your organization or PEAR support to be set as fleet admin.';
          } else if (!fleetOrg) {
            fleetCheckError = 'Fleet admin account has no organization. Contact PEAR support.';
          }
        }
      } catch (e) {
        if (!isSuperAdmin) fleetCheckError = 'Could not load your account. Try again or contact support.';
      }
      if (!isSuperAdmin && !isFleet) {
        await auth.signOut();
        throw new Error(fleetCheckError || 'You do not have admin access. Ask a PEAR admin to invite you to the console, or use your fleet admin account. Need help? pearnet104@gmail.com');
      }
      if (isFleet && !user.emailVerified) {
        await auth.signOut();
        throw new Error('Please verify your email first. Check your inbox for the verification link, then log in again.');
      }
      await ensureLocationDek(isSuperAdmin ? null : orgCode);
      setCurrentUser(user);
      setIsAdmin(true);
      setIsFleetAdmin(isFleet);
      setFleetOrgCode(orgCode);
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code === 'auth/invalid-credential' || code === 'auth/invalid-login-credentials' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
        throw new Error('Invalid email or password. Please check and try again.');
      }
      if (code === 'auth/user-disabled') {
        throw new Error('This account has been disabled. Contact support.');
      }
      if (code === 'auth/operation-not-allowed') {
        throw new Error('Email/password sign-in is not enabled for this app. Contact support.');
      }
      if (code === 'auth/too-many-requests') {
        throw new Error('Too many failed attempts. Try again later or reset your password.');
      }
      if (code === 'auth/invalid-email') {
        throw new Error('Please enter a valid email address.');
      }
      throw err;
    }
  };

  const logout = async () => {
    const uid = auth.currentUser?.uid;
    await auth.signOut();
    clearLocationDek();
    clearActiveOnLogout(uid);
    setCurrentUser(null);
    setIsAdmin(false);
    setIsFleetAdmin(false);
    setFleetOrgCode(null);
  };

  useEffect(() => {
    let prevUid: string | null = auth.currentUser?.uid || null;
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (prevUid && !user) clearActiveOnLogout(prevUid);
      prevUid = user?.uid || null;
      setCurrentUser(user);
      if (user) {
        await resolveAdminStatus(user);
      } else {
        clearLocationDek();
        setAuthReady(true);
      }
    });
    return () => unsub();
  }, []);

  const refreshAdminStatus = async () => {
    await resolveAdminStatus(auth.currentUser);
  };

  const value = {
    currentUser,
    login,
    logout,
    refreshAdminStatus,
    isAdmin,
    isFleetAdmin,
    fleetOrgCode,
    authReady
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 